/**
 * Kiosk Analytics - Automatic Mixpanel event tracking from XState machine
 *
 * Subscribes to the trace inspector to automatically send Mixpanel events
 * for every machine state transition and user action.
 *
 * Event format: {fromState}:{eventType}
 * Examples:
 *   - welcome:START
 *   - ocrLicense:SUBMIT
 *   - languageSelect:SUBMIT
 *
 * Metadata includes:
 *   - kiosk_session_id: Unique session for grouping in Mixpanel
 *   - kiosk_from_state: The state when the event was sent
 *   - kiosk_to_state: The state after transition (if available)
 *   - Plus any data from the machine event (e.g., { language: "en" })
 *
 * Usage:
 *   useEffect(() => {
 *     return enableKioskAnalytics();
 *   }, []);
 */

import { trackEvent } from "@frontend/lib/analytics";
import type { z } from "zod";
import { type TraceEvent, traceInspector } from "./trace-inspector";

// Import actors to extract input schemas
import { createAppointmentWithBoughtTreatmentsActor } from "../machine/actors/create-appointment-with-bought-treatments.actor";
import { createInsuranceActor } from "../machine/actors/create-insurance.actor";
import { createPaymentIntentActor } from "../machine/actors/create-payment-intent.actor";
import { createUserAndConsumerActor } from "../machine/actors/create-user-and-consumer.actor";
import { fetchPricingActor } from "../machine/actors/fetch-pricing-only.actor";
import { loadHrtsActor } from "../machine/actors/load-hrts.actor";
import { processPOSPaymentActor } from "../machine/actors/process-pos-payment.actor";
import { processStripeElementPaymentActor } from "../machine/actors/process-stripe-element-payment.actor";
import { sendSMSActor } from "../machine/actors/send-sms.actor";
import { submitIntakeFormsActor } from "../machine/actors/submit-intake-forms.actor";
import { verifyInsuranceActor } from "../machine/actors/verify-insurance.actor";
import { verifySMSActor } from "../machine/actors/verify-sms.actor";

// ============================================================================
// Event Tracking
// ============================================================================

const SKIP_EVENTS = new Set([
  "xstate.init",
  "xstate.stop",
  "xstate.after",
  "xstate.update",
]);

// PII fields to anonymize (from FullNameInput and DOBInput)
const PII_FIELDS = new Set(["firstName", "lastName", "dateOfBirth"]);

/**
 * Extract top-level keys from a Zod object schema
 */
function getZodObjectKeys(schema: z.ZodSchema): string[] {
  try {
    // Zod object schemas have a .shape property
    const shape = (schema as z.ZodObject<z.ZodRawShape>).shape;
    if (shape && typeof shape === "object") {
      return Object.keys(shape);
    }
  } catch {
    // Not an object schema or doesn't have shape
  }
  return [];
}

/**
 * Actor metadata with input schema
 */
interface ActorWithInputSchema {
  metadata: {
    inputSchema: z.ZodSchema;
  };
}

/**
 * Map of actor/state names to their actors with input schemas.
 * The key should match the state name in the machine (which is also the actor name).
 */
const ACTORS_WITH_SCHEMAS: Record<string, ActorWithInputSchema> = {
  createAppointmentWithBoughtTreatments:
    createAppointmentWithBoughtTreatmentsActor,
  createUserAndConsumer: createUserAndConsumerActor,
  createInsurance: createInsuranceActor,
  submittingIntakeForms: submitIntakeFormsActor,
  sendSMS: sendSMSActor,
  verifySMS: verifySMSActor,
  verifyInsurance: verifyInsuranceActor,
  fetchPricing: fetchPricingActor,
  loadHrts: loadHrtsActor,
  createPaymentIntent: createPaymentIntentActor,
  processPOSPayment: processPOSPaymentActor,
  processStripeElementPayment: processStripeElementPaymentActor,
};

/**
 * Get input keys for an actor by extracting from its Zod schema.
 * Returns undefined if actor not found or no schema available.
 */
function getActorInputKeys(actorName: string): string[] | undefined {
  const actor = ACTORS_WITH_SCHEMAS[actorName];
  if (!actor?.metadata?.inputSchema) {
    return undefined;
  }
  return getZodObjectKeys(actor.metadata.inputSchema);
}

/**
 * Anonymize PII fields in event data before sending to analytics
 */
function anonymizeEventData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const anonymized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (PII_FIELDS.has(key) && value !== undefined && value !== null) {
      // Replace with "[REDACTED]" to indicate presence without exposing value
      anonymized[key] = "[REDACTED]";
    } else {
      anonymized[key] = value;
    }
  }

  return anonymized;
}

let lastTrackedEventId: number | null = null;
let accumulatedContext: Record<string, unknown> = {};

/**
 * Pending event waiting to be combined with its transition.
 * Can be a user action (SUBMIT, SKIP) or an actor completion.
 */
let pendingEvent: {
  type: "user" | "actor";
  eventType: string;
  fromState: string;
  eventData?: Record<string, unknown>;
  actorName?: string;
  actorOutput?: Record<string, unknown>;
  sessionId: string;
} | null = null;

/**
 * Process a trace event and send to Mixpanel
 *
 * @param event - The trace event to process
 * @param sessionId - The trace/session ID for grouping events in Mixpanel
 */
function processTraceEvent(event: TraceEvent, sessionId: string): void {
  // Skip if already processed
  if (event.id === lastTrackedEventId) {
    return;
  }
  lastTrackedEventId = event.id;

  // Handle snapshot events (state transitions, e.g., actor completions)
  if (event.inspectionType === "@xstate.snapshot") {
    processSnapshotEvent(event, sessionId);
    return;
  }

  // Only track @xstate.event (user actions) beyond this point
  if (event.inspectionType !== "@xstate.event") {
    return;
  }

  const eventType = event.machineEvent?.type;

  // Handle actor completion events (xstate.done.actor.*)
  // Buffer them to combine with the subsequent transition.
  // Flush any pending user event first — this is the final action
  // (e.g. SUBMIT) in the child arc that drove it to completion.
  if (eventType?.startsWith("xstate.done.actor.") && event.actorName) {
    if (pendingEvent?.type === "user") {
      const toState = `${event.actorName}:done`;
      trackEvent({
        category: "interaction",
        context: "kiosk",
        action: `${pendingEvent.fromState}:${pendingEvent.eventType}→${toState}`,
        metadata: {
          kiosk_session_id: pendingEvent.sessionId,
          kiosk_from_state: pendingEvent.fromState,
          kiosk_to_state: toState,
          kiosk_event_type: pendingEvent.eventType,
          kiosk_practice_id: accumulatedContext.practiceId,
          environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
          tags: ["KIOSK"],
          ...(pendingEvent.eventData
            ? anonymizeEventData(pendingEvent.eventData)
            : {}),
        },
      });
    }

    pendingEvent = {
      type: "actor",
      eventType,
      fromState: event.fromState || "unknown",
      actorName: event.actorName,
      actorOutput: event.actorOutput,
      sessionId,
    };
    return;
  }

  if (
    !eventType ||
    SKIP_EVENTS.has(eventType) ||
    eventType.startsWith("xstate.")
  ) {
    return;
  }

  const fromState = event.fromState || "unknown";

  // Buffer user event - wait for snapshot to get toState before emitting
  pendingEvent = {
    type: "user",
    eventType,
    fromState,
    eventData:
      event.machineEvent?.data &&
      typeof event.machineEvent.data === "object" &&
      event.machineEvent.data !== null
        ? (event.machineEvent.data as Record<string, unknown>)
        : undefined,
    sessionId,
  };
}

/**
 * Process snapshot events (state transitions from actor completions, etc.)
 *
 * Combines pending user events with their resulting transitions.
 * Format: {fromState}:{eventType}→{toState}
 *
 * Also logs actor input when entering invoke states.
 */
function processSnapshotEvent(event: TraceEvent, sessionId: string): void {
  const { fromState, toState, contextDelta } = event;

  // Update accumulated context with delta
  if (contextDelta) {
    // Apply added keys
    Object.assign(accumulatedContext, contextDelta.added);
    // Apply changed keys (use new values)
    for (const [key, { to }] of Object.entries(contextDelta.changed)) {
      accumulatedContext[key] = to;
    }
    // Remove deleted keys
    for (const key of contextDelta.removed) {
      delete accumulatedContext[key];
    }
  }

  // Only track actual state transitions (not context-only updates)
  if (!fromState || !toState || fromState === toState) {
    return;
  }

  // Check if there's a pending event to combine with this transition
  if (pendingEvent && pendingEvent.fromState === fromState) {
    if (pendingEvent.type === "user") {
      // Emit combined event: user action + transition
      const metadata: Record<string, unknown> = {
        kiosk_session_id: sessionId,
        kiosk_from_state: fromState,
        kiosk_to_state: toState,
        kiosk_event_type: pendingEvent.eventType,
        kiosk_practice_id: accumulatedContext.practiceId,
        environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
        tags: ["KIOSK"],
        // Include event data (with PII anonymized)
        ...(pendingEvent.eventData
          ? anonymizeEventData(pendingEvent.eventData)
          : {}),
      };

      // Combined format: {fromState}:{eventType}→{toState}
      const action = `${fromState}:${pendingEvent.eventType}→${toState}`;

      trackEvent({
        category: "interaction",
        context: "kiosk",
        action,
        metadata,
      });
    } else if (pendingEvent.type === "actor") {
      // Emit combined event: actor completion + transition
      const metadata: Record<string, unknown> = {
        kiosk_session_id: sessionId,
        kiosk_from_state: fromState,
        kiosk_to_state: toState,
        actor_name: pendingEvent.actorName,
        kiosk_practice_id: accumulatedContext.practiceId,
        environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
        tags: ["KIOSK", "ACTOR"],
      };

      // Include anonymized actor output
      if (pendingEvent.actorOutput) {
        metadata.actor_output = anonymizeEventData(pendingEvent.actorOutput);
      }

      // Combined format: actor:{actorName}→{toState}
      const action = `actor:${pendingEvent.actorName}→${toState}`;

      trackEvent({
        category: "interaction",
        context: "kiosk",
        action,
        metadata,
      });
    }

    // Clear pending event
    pendingEvent = null;
  } else {
    // No pending event - this is an automatic transition (guard, always)
    const metadata: Record<string, unknown> = {
      kiosk_session_id: sessionId,
      kiosk_from_state: fromState,
      kiosk_to_state: toState,
      kiosk_practice_id: accumulatedContext.practiceId,
      environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
      tags: ["KIOSK", "AUTO"],
    };

    // Format for automatic transitions: {fromState}→{toState}
    const action = `${fromState}→${toState}`;

    trackEvent({
      category: "interaction",
      context: "kiosk",
      action,
      metadata,
    });
  }

  // Check if entering an invoke state - log actor input
  const inputKeys = getActorInputKeys(toState);
  if (inputKeys && inputKeys.length > 0) {
    processActorInputEvent(toState, inputKeys, sessionId);
  }
}

/**
 * Process actor input event (when entering an invoke state)
 *
 * Extracts relevant context keys and logs them as actor input.
 * Format: actor:{stateName}:input
 */
function processActorInputEvent(
  stateName: string,
  inputKeys: string[],
  sessionId: string,
): void {
  // Extract input values from accumulated context
  const actorInput: Record<string, unknown> = {};
  for (const key of inputKeys) {
    if (key in accumulatedContext) {
      actorInput[key] = accumulatedContext[key];
    }
  }

  const metadata: Record<string, unknown> = {
    kiosk_session_id: sessionId,
    kiosk_state: stateName,
    kiosk_practice_id: accumulatedContext.practiceId,
    environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
    tags: ["KIOSK", "ACTOR"],
    actor_input: anonymizeEventData(actorInput),
  };

  // Format: actor:{stateName}:input
  const action = `actor:${stateName}:input`;

  trackEvent({
    category: "interaction",
    context: "kiosk",
    action,
    metadata,
  });
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Enable automatic Mixpanel tracking for kiosk machine events.
 * Returns an unsubscribe function.
 *
 * Usage:
 *   useEffect(() => {
 *     return enableKioskAnalytics();
 *   }, []);
 */
export function enableKioskAnalytics(): () => void {
  // Reset state
  accumulatedContext = {};
  pendingEvent = null;

  return traceInspector.subscribe((trace) => {
    // Initialize accumulated context from initial context if empty
    if (Object.keys(accumulatedContext).length === 0 && trace.initialContext) {
      accumulatedContext = { ...trace.initialContext };
    }

    const latestEvent = trace.events[trace.events.length - 1];
    if (latestEvent) {
      processTraceEvent(latestEvent, trace.traceId);
    }
  });
}

/**
 * Manually track a kiosk interaction (for UI events outside the machine)
 */
export function trackKioskInteraction(
  action: string,
  metadata: Record<string, unknown> = {},
): void {
  trackEvent({
    category: "interaction",
    context: "kiosk",
    action,
    metadata,
  });
}
